import { useEffect, useMemo } from "react";
import { Link } from "react-router";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { setTitle } from "@/hooks/useHeader.ts";
import { usePrizeCompetition } from "@/hooks/usePrizeCompetition.ts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { Button } from "@/components/ui/button.tsx";
import CountdownTimer from "@/components/CountdownTimer.tsx";
import { useTranslation } from "@/i18n";

const FreeTicket = () => {
  const { t } = useTranslation();
  const { competition, claimInstruction, loading, error } =
    usePrizeCompetition();

  useEffect(() => {
    setTitle(t("freeTicket.title"));
  }, [t]);

  const endDateString = competition?.draw.end_date;
  const endDate = useMemo(
    () => (endDateString ? new Date(endDateString) : undefined),
    [endDateString],
  );

  if (loading && !competition) {
    return <LoaderCircle className="mx-auto size-6 animate-spin" />;
  }

  if (error && !competition) {
    return (
      <Alert variant="destructive" className="mx-auto my-4 w-fit">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("common.somethingWentWrong")}</AlertTitle>
        <AlertDescription>{t("freeTicket.fetchFailed")}</AlertDescription>
      </Alert>
    );
  }

  if (!competition || !claimInstruction) {
    return <div className="m-4 text-center">{t("freeTicket.empty")}</div>;
  }

  return (
    <div className="mx-auto flex w-full flex-col lg:max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">
            {competition.name}
          </CardTitle>
          {competition.description && (
            <CardDescription>{competition.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div
            className="rich-text"
            dangerouslySetInnerHTML={{ __html: claimInstruction }}
          />
        </CardContent>
        <CardFooter className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold">{competition.draw.name}</div>
            <CountdownTimer
              time={endDate!}
              compact={true}
              className="text-primary mt-1"
            />
          </div>
          <Link to={`/raffles/${competition.draw.id}`}>
            <Button className="w-full">{t("freeTicket.viewRaffle")}</Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default FreeTicket;
